import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AFRegistrationDomain } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { EventService } from '../service/event.service';
import { ShowErrorEvent } from '../service/events';

interface ResetPassowrdFormModel {
  newPassword: FormControl<string>,
  confirmPassword: FormControl<string>
}

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false
})
export class ResetPasswordComponent implements OnInit {

  user: any;
  formModel: AFRegistrationDomain;
  showError: boolean = false;
  showPasswordHint: boolean = false;
  showReEnterPasswordHint: boolean = false;
  hide = true;
  loginType: number;
  email: string;
  resetPasswordGroup: UntypedFormGroup;

  constructor(private readonly router: Router, private readonly fb: FormBuilder, private readonly userService: AuthService, private readonly eventBus: EventService, private readonly _snackBar: MatSnackBar) {
  }

  get newPassword() { return this.resetPasswordGroup.get('newPassword') };
  get confirmPassword() { return this.resetPasswordGroup.get('confirmPassword') };

  ngOnInit() {
    this.createGroup();

    this.loginType = Number(localStorage.getItem('loginType'));

    if (this.loginType === 4) {
      this.user = this.userService.getVendorUser();
      this.email = this.user.username;
    } else {
      this.user = this.userService.getUser();
      this.email = this.user.email;
    }
  }

  createGroup(): void {
    this.resetPasswordGroup = this.fb.group<ResetPassowrdFormModel>({
      newPassword: this.fb.control('', [Validators.required, Validators.minLength(8), Validators.maxLength(20), Validators.pattern('(?:[0-9]+[a-z A-Z]|[a-z A-Z]+[0-9])[a-z A-Z 0-9]*')]),
      confirmPassword: this.fb.control('', [Validators.required])
    }, {
      validators: [passwordValidator()]
    });
  }

  onSubmit() {
    let resetPasswordDomain = new AFRegistrationDomain();
    resetPasswordDomain.verifyId = this.userService.getVerifyId();
    resetPasswordDomain.vendorSign = (this.loginType == 4);

    if (resetPasswordDomain.verifyId) {
      if (this.loginType === 4) {
        resetPasswordDomain.username = this.user.username;
      } else {
        resetPasswordDomain.username = this.user.email;
      }

      resetPasswordDomain.password = this.newPassword.value;
      resetPasswordDomain.rePassword = this.confirmPassword.value;
      let observable: Observable<AFRegistrationDomain>;

      if (this.loginType == 4) {
        observable = this.userService.resetVendorPassword(resetPasswordDomain);
      } else {
        observable = this.userService.resetPassword(resetPasswordDomain);
      }

      observable.subscribe({
        next: (data: AFRegistrationDomain) => {
          if (data) {
            this.formModel = data;
            if (data.errorMessage === "success") {
              this._snackBar.open("Password changed successfully.", "Close", {
                duration: 4000,
              });
              this.router.navigate(['successPassword']);
            } else if (data.errorMessage === "unauthorized") {
              this.router.navigate(['unauthorized']);
            } else if (data.errorMessage) {
              this.eventBus.emit(new ShowErrorEvent({ httpStatus: 1, text: data.errorMessage }));
              this.showError = !!data.errorMessage;
            }
          }
        },
        error: (error: any) => console.log(error)
      });
    }
  }

  navigateLogin() {
    this.router.navigate(['login']);
  }

  checkSpecialCharacters(str: string) {
    const regex = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g;
    return !regex.test(str);
  }

  checkAlphaCharacter(str: string) {
    const regex = /[a-zA-Z]/;
    return regex.test(str);
  }

  checkNumeric(str: string) {
    const regex = /\d/;
    return regex.test(str);
  }

  showHint() {
    this.showPasswordHint = !this.showPasswordHint;
  }

  showHintTwo() {
    this.showReEnterPasswordHint = !this.showReEnterPasswordHint;
  }

  generatePassword() {
    let i: number;
    let randPass: string;
    let loop: boolean = false;
    let values: Uint32Array;
    const charSet: string = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const keywords = /(APPL|ASDF|BASIC|CADAM|DEMO|FOCUS|GAME|IBM|LOG|NATION|NCNB|NET|NEW|MONEY|RESET|ROS|SIGN|SOVRAN|SYS|TEST|TSO|VALID|VTAM|XXX|1234|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/i;

    do {
      randPass = "";
      values = new Uint32Array(8);
      window.crypto.getRandomValues(values);

      for (i = 0; i < 8; ++i) {
        randPass += charSet[values[i] % charSet.length];
      }

      const alphaNumRegex = /^(?=.*[a-zA-Z])(?=.*\d)/;
      if (alphaNumRegex.exec(randPass) && !keywords.exec(randPass)) {
        this.resetPasswordGroup.get('newPassword').setValue(randPass);
        loop = false;
      } else {
        loop = true;
      }
    } while (loop);
  }
}

export function passwordValidator(): ValidatorFn {
  return (control: UntypedFormGroup): ValidationErrors | null => {
    const errors: ValidationErrors = {};
    const newPasswordControl = control.get('newPassword');
    const confirmPasswordControl = control.get('confirmPassword');

    if (newPasswordControl.value && confirmPasswordControl.value && (newPasswordControl.value != confirmPasswordControl.value)) {
      confirmPasswordControl.setErrors({ noMatch: true });
    }

    return Object.keys(errors).length ? errors : null;
  };
}
